import { memo, useCallback, type FC, type ReactNode } from "react";
import {
  Braces,
  CircleX,
  ExternalLink,
  ImageIcon,
  Info,
  MousePointerClick,
  TriangleAlert,
} from "lucide-react";
import { Trans, useTranslation } from "react-i18next";
import { formatNumber } from "src/utils/numberFormat";
import { META_TEMPLATES_URL, WHATSAPP_EDITOR_KEY } from "../WhatsAppCampaignConstants";
import type { WhatsAppTemplate } from "../WhatsAppCampaignTypes";
import { formatTemplateName } from "../WhatsAppCampaignUtils";
import { MessageMeta, TemplateStatusBadge, WhatsAppMessageText } from "./WhatsAppMessageParts";

const CARD_KEY = `${WHATSAPP_EDITOR_KEY}.card`;

interface WhatsAppTemplateCardProps {
  template: WhatsAppTemplate;
  onUse: (_template: WhatsAppTemplate) => void;
}

const WhatsAppTemplateCard: FC<WhatsAppTemplateCardProps> = ({ template, onUse }) => {
  const { t, i18n } = useTranslation();
  const { status, body, canUse, usedInCampaign } = template;
  const title = formatTemplateName(template.name);
  const handleUse = useCallback(() => onUse(template), [onUse, template]);

  const renderApprovedNotice = (): ReactNode => {
    const { variableKeys, buttons, mediaType, unsupportedReasons } = template;
    return (
      <div className="wa-template-card__details">
        {variableKeys.length > 0 && (
          <p className="wa-template-card__notice">
            <Braces className="wa-template-card__notice-icon" aria-hidden />
            {t(`${CARD_KEY}.variables`, {
              count: variableKeys.length,
              formattedCount: formatNumber(variableKeys.length, {}, i18n.language),
            })}
          </p>
        )}
        {mediaType && (
          <p className="wa-template-card__notice">
            <ImageIcon className="wa-template-card__notice-icon" aria-hidden />
            {t(`${WHATSAPP_EDITOR_KEY}.media.label`)}
          </p>
        )}
        {buttons.length > 0 && (
          <p className="wa-template-card__notice">
            <MousePointerClick className="wa-template-card__notice-icon" aria-hidden />
            {t(`${CARD_KEY}.buttons`, {
              count: buttons.length,
              formattedCount: formatNumber(buttons.length, {}, i18n.language),
              names: buttons
                .map(button =>
                  t(`${WHATSAPP_EDITOR_KEY}.buttonKinds.${button.kind}`, {
                    defaultValue: button.kind,
                  })
                )
                .join(", "),
            })}
          </p>
        )}
        {unsupportedReasons.map(reason => (
          <p key={reason} className="wa-template-card__notice wa-template-card__notice--error">
            <TriangleAlert className="wa-template-card__notice-icon" aria-hidden />
            {reason}
          </p>
        ))}
      </div>
    );
  };

  const renderUnavailableNotice = (): ReactNode => (
    <p className="wa-template-card__notice wa-template-card__notice--info">
      <Info className="wa-template-card__notice-icon" aria-hidden />
      <span>
        {usedInCampaign ? (
          <Trans
            i18nKey={`${CARD_KEY}.unavailableNotice`}
            values={{ campaign: usedInCampaign }}
            components={{ b: <strong /> }}
          />
        ) : (
          t(`${CARD_KEY}.unavailableGenericNotice`)
        )}
      </span>
    </p>
  );

  const renderNotice = (): ReactNode => {
    switch (status) {
      case "approved":
        return renderApprovedNotice();
      case "pending":
        return (
          <p className="wa-template-card__notice wa-template-card__notice--info">
            <Info className="wa-template-card__notice-icon" aria-hidden />
            {t(`${CARD_KEY}.pendingNotice`)}
          </p>
        );
      case "unavailable":
        return renderUnavailableNotice();
      default:
        return (
          <p className="wa-template-card__notice wa-template-card__notice--error">
            <CircleX className="wa-template-card__notice-icon" aria-hidden />
            {template.rejectionReason
              ? t(`${CARD_KEY}.rejectedNotice`, { reason: template.rejectionReason })
              : t(`${CARD_KEY}.rejectedNoticeNoReason`)}
          </p>
        );
    }
  };

  const metaActionKey = {
    approved: "",
    pending: `${CARD_KEY}.viewInMeta`,
    unavailable: usedInCampaign ? `${CARD_KEY}.recreateInMeta` : `${CARD_KEY}.viewInMeta`,
    rejected: `${CARD_KEY}.fixAndResubmit`,
  }[status];

  return (
    <article className="wa-template-card">
      <header className="wa-template-card__header">
        <div className="wa-template-card__heading">
          <h4 className="wa-template-card__title">{title}</h4>
          {status === "pending" && (
            <span className="wa-template-card__subtitle">{t(`${CARD_KEY}.awaitingReview`)}</span>
          )}
        </div>
        <TemplateStatusBadge status={status} />
      </header>

      <div className="wa-template-card__preview">
        <span className="wa-template-card__preview-label">{t(`${CARD_KEY}.preview`)}</span>
        {body && <WhatsAppMessageText body={body} className="wa-template-card__body" />}
        <MessageMeta status={status} />
      </div>

      {renderNotice()}

      {status === "approved" ? (
        <button
          type="button"
          className="button button--primary wa-template-card__action"
          onClick={handleUse}
          disabled={!canUse}
          aria-label={t(`${CARD_KEY}.useLabel`, { name: title })}
        >
          {t(`${CARD_KEY}.useTemplate`)}
        </button>
      ) : (
        <a
          href={template.metaUrl ?? META_TEMPLATES_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="wa-template-card__action wa-template-card__action--outline"
        >
          {t(metaActionKey)}
          <ExternalLink className="wa-template-card__action-icon" aria-hidden />
        </a>
      )}
    </article>
  );
};

export default memo(WhatsAppTemplateCard);
